import { Accordion, Button, Group, Stack, Tabs } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconClipboardList,
  IconDeviceFloppy,
  IconEdit,
  IconHistory,
  IconInfoCircle,
  IconPlus,
} from "@tabler/icons-react";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_ERROR_TITLE,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
} from "common/constants";
import React, { startTransition, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useForm } from "@mantine/form";
import { IconStack2, IconTag } from "@tabler/icons-react";
import { AdminProduct } from "types/model.types";

import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getProduct, submitProduct } from "api/product.service";
import { useProductStore } from "stores/useProductStore";
import AdminProductOrderHistory from "./AdminProductOrderHistory";
import AdminProductTransactions from "./AdminProductTransactions";
import ProductDetailsCard1 from "./cards/ProductDetailsCard1";
import ProductDetailsCard2 from "./cards/ProductDetailsCard2";
import ProductDetailsCard3 from "./cards/ProductDetailsCard3";

export const AdminProductEnquiry: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [readonly, setReadonly] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("details");
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const navigate = useNavigate();
  const allAccordionKeys = ["details", "pricing", "stock"];
  const syncModifiedProducts = useProductStore((s) => s.syncModifiedProducts);
  const initialValues = useMemo(
    () => ({
      id: 0,
      closed: false,
      productCode: "0",
      supplierCode: "",
      altCode: "",
      productDescription: "",
      measure: "",
      barcode: "",
      barcodeCtn: "",
      noDiscount: true,
      duty: 0,
      freight: 0,
      freightCtn: 0,
      volumne: 0,
      volumneCtn: 0,
      sellPrice1: 0,
      sellPrice2: 0,
      sellPrice3: 0,
      sellPrice4: 0,
      costPrice: 0,
      retail: 0,
      onHand: 0,
      onHandCtn: 0,
      ordered: 0,
      backOrder: 0,
      pendingOrder: 0,
      confirmedOrder: 0,
      salesMtd: 0,
      salesYtd: 0,
      costMtd: 0,
      costYtd: 0,
      quanMtd: 0,
      quanYtd: 0,
      gp1: 0,
      gp2: 0,
      gp3: 0,
      gp4: 0,
      units: 1,
      unitsCtn: 1,
    }),
    [],
  );

  const maxLen = (len: number) => (value: string) =>
    value && value.length > len ? `Max length is ${len}` : null;

  const isDate = (value: string) =>
    value && isNaN(Date.parse(value)) ? "Invalid date" : null;
  const { allProducts, isLoading: productsLoading } = useProductStore();

  const form = useForm<AdminProduct>({
    mode: "controlled",
    initialValues,
    validate: {
      productDescription: maxLen(255),
      unitsCtn: (value: number | string) => {
        const num = Number(value);

        if (value === "" || value === undefined || value === null)
          return "Units per carton is required";

        if (Number.isNaN(num)) return "Units per carton must be a number";

        if (num <= 0) return "Units per carton must be greater than 0";

        return null;
      },
    },
  });
  // Extract stable helpers from form
  const { values, setFieldValue, getInputProps } = form;
  // Memoize the props passed to children so they stay stable
  const childFormApi = useMemo(
    () => ({
      values,
      setFieldValue,
      getInputProps,
    }),
    [values],
  ); // only changes when actual values change

  useEffect(() => {
    if (id) return;

    if (allProducts && allProducts?.items.length > 0) {
      const p = allProducts.items[0];
      navigate(`/admin/enquire-products?id=${p.productCode}`);
    }
  }, [allProducts && allProducts?.items]);

  useEffect(() => {
    if (!id) return;

    const newId = Number(id);
    if (!readonly && recordId !== undefined && newId !== recordId) {
      notifications.show({
        title: MSG_DEFAULT_ERROR_TITLE,
        message:
          "You are in edit mode. Please save existing data before loading different record(s)",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    setRecordId(newId);
  }, [id, readonly, recordId]);

  // Load product data
  useEffect(() => {
    if (!recordId) return;

    let mounted = true;

    const fetchProduct = async () => {
      const response = await safeApiCall(getProduct(recordId));
      if (response && mounted) {
        startTransition(() => {
          requestAnimationFrame(() => {
            form.setValues(response.data);
          });
        });
      }
    };

    fetchProduct();
    return () => {
      mounted = false;
    };
  }, [recordId]);

  const handleAdd = async () => {
    startTransition(() => {
      requestAnimationFrame(() => {
        form.setValues(initialValues);
        setReadonly(false);
        setIsNew(true);
      });
    });
  };
  const handleSave = async () => {
    const unitsCtn = Number(form.getValues().unitsCtn);
    if (Number.isNaN(unitsCtn)) {
      notifications.show({
        title: "Validation Error",
        message: "Units per carton must be a number",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    if (unitsCtn <= 0) {
      notifications.show({
        title: "Validation Error",
        message: "Units per carton must be greater than 0",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }
    const validation = form.validate();
    if (validation.hasErrors) {
      notifications.show({
        title: "Validation Error",
        message: "Please fix the highlighted fields.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }
    setSaving(true);

    const payload = {
      ...form.getValues(),
      requestId: isNew ? Number(form.values.productCode) : 0,
    };
    try {
      const response = await safeApiCall(submitProduct(payload));
      if (response && response?.error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: response?.error,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
        return;
      }

      const formData = form.getValues();
      const onHandCtn = Math.floor(
        (formData.onHand ?? 0) / (form.getValues().unitsCtn ?? 1),
      );

      // Return to readonly mode
      startTransition(() => {
        syncModifiedProducts();
        setReadonly(true);
        setIsNew(false);
        setActiveTab("details");
        form.setValues({
          onHandCtn: onHandCtn,
        });
      });

      const responseId: number = response?.id;
      if (responseId !== Number(id)) {
        navigate(`/admin/enquire-products?id=${responseId}`);
        return;
      }

      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (error) {
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Stack>
        <Group justify="flex-start" wrap="nowrap" mb="md">
          <Button
            leftSection={<IconPlus size={16} />}
            disabled={!readonly || saving}
            onClick={handleAdd}
          >
            New Product
          </Button>
          <Button
            disabled={saving || recordId === undefined}
            leftSection={
              readonly ? <IconEdit size={16} /> : <IconDeviceFloppy size={16} />
            }
            onClick={
              readonly
                ? () => {
                    startTransition(() => {
                      setReadonly(false);
                      setActiveTab("details");
                    });
                  }
                : handleSave
            }
          >
            {readonly ? "Edit Product" : "Save Product Details"}
          </Button>
        </Group>
      </Stack>

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab
            value="details"
            title="Product Details"
            leftSection={<IconInfoCircle size={14} />}
          >
            Product Details
          </Tabs.Tab>

          <Tabs.Tab
            title="Customer Order History"
            value="transactions"
            leftSection={<IconClipboardList size={14} />}
          >
            Transaction Histories
          </Tabs.Tab>

          <Tabs.Tab
            value="history"
            title="Supplier Order History"
            leftSection={<IconHistory size={14} />}
          >
            Stock Order History
          </Tabs.Tab>
        </Tabs.List>

        {/* DETAILS TAB */}
        <Tabs.Panel value="details">
          <Accordion
            multiple={true} // readonly → single-open mode
            value={allAccordionKeys}
            defaultValue={["details"]}
          >
            <Accordion.Item value="details">
              <Accordion.Control icon={<IconInfoCircle size={16} />}>
                Details
              </Accordion.Control>
              <Accordion.Panel>
                <ProductDetailsCard1
                  readonly={readonly}
                  formApi={childFormApi}
                />
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="pricing">
              <Accordion.Control icon={<IconTag size={16} />}>
                Pricing & Stock Levels
              </Accordion.Control>
              <Accordion.Panel>
                <ProductDetailsCard2
                  readonly={readonly}
                  formApi={childFormApi}
                />
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="stock">
              <Accordion.Control icon={<IconStack2 size={16} />}>
                Sales Figures
              </Accordion.Control>
              <Accordion.Panel>
                <ProductDetailsCard3
                  readonly={readonly}
                  formApi={childFormApi}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Tabs.Panel>

        {/* OTHER TABS */}
        <Tabs.Panel value="transactions">
          <AdminProductTransactions />
        </Tabs.Panel>

        <Tabs.Panel value="history">
          <AdminProductOrderHistory />
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default AdminProductEnquiry;
